import { supabase } from '../lib/supabase';

/**
 * Centralized role management service to ensure consistent role-based access control
 */
export const roleService = {
  // Get current user's role from database (not metadata)
  async getCurrentUserRole() {
    try {
      // Get user with better error handling
      let user = null;
      try {
        const { data: { user: authUser }, error: authError } = await supabase?.auth?.getUser();
        if (authError) {
          console.warn('Auth error in getCurrentUserRole:', authError);
          return 'employee';
        }
        user = authUser;
      } catch (authErr) {
        console.warn('Failed to get authenticated user:', authErr);
        return 'employee';
      }

      if (!user || !user?.id) return 'employee'; // Check for both user and user.id

      // Fetch user profile with enhanced error handling
      try {
        const { data: userProfile, error } = await supabase
          ?.from('user_profiles')?.select('role')?.eq('id', user?.id) // Use user.id directly (not optional chaining)
          ?.single();

        if (error) {
          console.error('Error fetching user role:', error);
          return 'employee'; // Default to employee on error
        }

        return userProfile?.role || 'employee';
      } catch (dbError) {
        // Handle any database/network errors gracefully
        console.warn('Database query failed in getCurrentUserRole:', dbError?.message || dbError);
        return 'employee';
      }
    } catch (error) {
      // Catch-all for any unexpected errors
      console.error('Unexpected error in getCurrentUserRole:', {
        message: error?.message || 'Unknown error',
        name: error?.name,
        stack: error?.stack
      });
      return 'employee';
    }
  },

  // Check if current user has admin privileges
  async isAdmin() {
    const role = await this.getCurrentUserRole();
    return role === 'admin';
  },

  // Check if current user has HR privileges
  async isHR() {
    const role = await this.getCurrentUserRole();
    return role === 'hr_manager';
  },

  // Check if current user has admin or HR privileges
  async isAdminOrHR() {
    const role = await this.getCurrentUserRole();
    return role === 'admin' || role === 'hr_manager';
  },

  // Check if current user is employee
  async isEmployee() {
    const role = await this.getCurrentUserRole();
    return role === 'employee';
  },

  // Check if current user is supervisor
  async isSupervisor() {
    const role = await this.getCurrentUserRole();
    return role === 'supervisor';
  },

  // Check if user has permission for specific action
  async hasPermission(requiredRoles = []) {
    if (!requiredRoles || requiredRoles?.length === 0) return true;

    const currentRole = await this.getCurrentUserRole();
    return requiredRoles?.includes(currentRole);
  },

  // Get user's employee record
  async getCurrentUserEmployeeRecord() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;

      const { data: employee, error } = await supabase
        ?.from('employees')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        console.error('Error fetching employee record:', error);
        return null;
      }

      return employee;
    } catch (error) {
      console.error('Error in getCurrentUserEmployeeRecord:', error);
      return null;
    }
  },

  // Check if user can access admin features
  async canAccessAdminFeatures() {
    return await this.isAdminOrHR();
  },

  // Check if user can manage employees
  async canManageEmployees() {
    return await this.isAdminOrHR();
  },

  // Check if user can manage attendance (all employees)
  async canManageAttendance() {
    return await this.isAdminOrHR();
  },

  // Check if user can approve leave requests
  async canApproveLeaves() {
    return await this.isAdminOrHR();
  },

  // Check if user can view all employee data
  async canViewAllEmployees() {
    return await this.isAdminOrHR();
  },

  // Get appropriate dashboard route based on role
  async getDashboardRoute() {
    const isAdminOrHR = await this.isAdminOrHR();
    return isAdminOrHR ? '/admin-dashboard' : '/employee-dashboard';
  },

  // Validate role and redirect if necessary
  async validateRoleAccess(requiredRoles = [], currentPath = '') {
    const hasAccess = await this.hasPermission(requiredRoles);
    
    if (!hasAccess) {
      const redirectPath = await this.getDashboardRoute();
      return { hasAccess: false, redirectPath };
    }

    return { hasAccess: true, redirectPath: null };
  }
};

export default roleService;